import React from "react";
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  Paper,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import dayjs from "dayjs";
import dayOfYear from "dayjs/plugin/dayOfYear";
import { StyledButtonMain } from "../common/Buttons";
import EngagementsDashboard from "./EngagementsDashboard";
import EngagementsSearchResult from "./EngagementsSearchResult";
import { axiosInstance } from "../../utils/networking";
import {
  clearRecent,
  getRecent,
  pushRecent,
  removeRecent,
} from "../../utils/recentSearches";
dayjs.extend(dayOfYear);

const Engagements = () => {
  const [searchTerm, setSearchTerm] = React.useState("");
  const [searchFlag, setSearchFlag] = React.useState(false);
  const [recentSearches, setRecentSearches] =
    React.useState<string[]>(getRecent());
  const [searchResults, setSearchResults] = React.useState<{
    case_results: any[];
    activity_results: any[];
    file_results: any[];
  }>({
    case_results: [],
    activity_results: [],
    file_results: [],
  });

  const handleSearch = (term?: string) => {
    const q = (term ?? searchTerm).trim();
    if (!q) return;
    if (term) setSearchTerm(term);
    setSearchFlag(true);
    axiosInstance
      .post("/search", { searchTerm: q })
      .then((response) => {
        const {
          case_results,
          activity_results,
          file_results = [],
        } = response.data;
        // Server (Elasticsearch) ranking is authoritative — the old client-side
        // Fuse.js re-rank existed to compensate for the round-robin merge across
        // Azure/ES/Mongo, which is gone. Re-ranking here would only fight the
        // layered relevance the ES query already produces.
        setSearchResults({
          case_results,
          activity_results,
          file_results,
        });
        pushRecent(q);
        setRecentSearches(getRecent());
      })
      .catch((error) => {
        console.error("Error during search:", error);
      });
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") handleSearch();
  };

  const handleDeleteRecent = (term: string) => {
    removeRecent(term);
    setRecentSearches(getRecent());
  };

  const handleClearAll = () => {
    clearRecent();
    setRecentSearches([]);
  };

  return (
    <>
      <Box sx={{ width: "100%", marginTop: "-55px", paddingBottom: "15px" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Autocomplete
            freeSolo
            disableClearable
            options={recentSearches}
            inputValue={searchTerm}
            onInputChange={(_, value) => setSearchTerm(value)}
            onChange={(_, value) => {
              if (typeof value === "string" && value.trim()) {
                handleSearch(value.trim());
              }
            }}
            onFocus={() => setRecentSearches(getRecent())}
            sx={{ width: "50%" }}
            PaperComponent={(props) => (
              <Paper {...props}>
                {props.children}
                {recentSearches.length > 0 && (
                  <Box
                    sx={{
                      borderTop: "1px solid",
                      borderColor: "divider",
                      px: 1,
                      py: 0.5,
                    }}
                  >
                    <Button
                      size="small"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleClearAll();
                      }}
                      sx={{ fontSize: 12, color: "text.secondary" }}
                    >
                      Clear recent searches
                    </Button>
                  </Box>
                )}
              </Paper>
            )}
            renderOption={(props, option) => (
              <li
                {...props}
                key={option}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span>{option}</span>
                <IconButton
                  size="small"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDeleteRecent(option);
                  }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                size="small"
                placeholder="Search cases..."
                onKeyDown={handleKeyDown}
                sx={{ background: "white" }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {params.InputProps.endAdornment}
                      <IconButton
                        aria-label="search"
                        edge="end"
                        onClick={() => handleSearch()}
                      >
                        <SearchIcon />
                      </IconButton>
                    </>
                  ),
                }}
              />
            )}
          />
          <StyledButtonMain
            variant="contained"
            color="primary"
            onClick={() => handleSearch()}
          >
            Search
          </StyledButtonMain>
          {searchFlag && (
            <StyledButtonMain
              variant="contained"
              color="primary"
              onClick={() => setSearchFlag(false)}
            >
              Clear Search
            </StyledButtonMain>
          )}
        </Box>
      </Box>
      <Box sx={{ display: searchFlag ? "block" : "none" }}>
        <EngagementsSearchResult
          searchTerm={searchTerm}
          searchResults={searchResults}
        />
      </Box>
      <Box sx={{ display: searchFlag ? "none" : "block" }}>
        <EngagementsDashboard />
      </Box>
    </>
  );
};

export default Engagements;
